/**
 * APK Config Service
 * 
 * Shared APK configuration logic extracted from APKConfigPanel.tsx
 * to break circular dependency between admin-panel and services chunks.
 * 
 * This file provides:
 * - APKConfig type definition
 * - DEFAULT_APK_CONFIG defaults
 * - getAPKConfig() - read from localStorage
 * - getAPKConfigFromFirebase() - fetch live config from Firebase
 * - clearFirebaseConfigCache() - clear cached Firebase config
 * - clearAllCaches() - clear all application caches
 * - performCacheBustedRefresh() - force reload with cache bust
 */

export interface APKConfig {
    downloadUrl: string;
    googleDriveFolderId?: string;
    googleDriveApiKey?: string;
    autoFetchLatest?: boolean;
    version: string;
    versionCode: number;
    releaseDate: string;
    fileSize: string;
    minAndroidVersion: string;
    changelog: string[];
    isEnabled: boolean;
    forceUpdate: boolean;
    updateMessage: string;
    features: {
        pushNotifications: boolean;
        hapticFeedback: boolean;
        offlineMode: boolean;
        biometricAuth: boolean;
        backgroundSync: boolean;
        deepLinking: boolean;
    };
}

export const DEFAULT_APK_CONFIG: APKConfig = {
    downloadUrl: '',
    googleDriveFolderId: '',
    googleDriveApiKey: '',
    autoFetchLatest: true,
    version: '3.0.0',
    versionCode: 30,
    releaseDate: new Date().toISOString().split('T')[0],
    fileSize: '~25MB',
    minAndroidVersion: '7.0 (Nougat)',
    changelog: [
        '60fps smooth performance',
        'Push notifications for study reminders',
        'Haptic feedback on interactions',
        'Offline mode support',
        'Native sharing capabilities',
    ],
    isEnabled: true,
    forceUpdate: false,
    updateMessage: 'A new version of Luca is available! Update now for the best experience.',
    features: {
        pushNotifications: true,
        hapticFeedback: true,
        offlineMode: true,
        biometricAuth: false,
        backgroundSync: true,
        deepLinking: true,
    },
};

// Helper function to get APK config from localStorage (bundled at build time)
export function getAPKConfig(): APKConfig {
    try {
        const saved = localStorage.getItem('luca_apk_config');
        if (saved) {
            return { ...DEFAULT_APK_CONFIG, ...JSON.parse(saved) };
        }
    } catch (e) {
        console.error('[APKConfig] Failed to load config');
    }
    return DEFAULT_APK_CONFIG;
}

/**
 * Fetch APK config from Firebase (LIVE - for APK users)
 * This allows APK to follow admin config changes without needing an update
 * Uses cache with 5-minute expiry for performance
 */
export async function getAPKConfigFromFirebase(): Promise<APKConfig> {
    try {
        const CACHE_KEY = 'luca_apk_config_firebase';
        const CACHE_EXPIRY_KEY = 'luca_apk_config_firebase_expiry';
        const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

        // Check cache first
        const cacheExpiry = localStorage.getItem(CACHE_EXPIRY_KEY);
        const cachedConfig = localStorage.getItem(CACHE_KEY);

        if (cacheExpiry && cachedConfig && Date.now() < parseInt(cacheExpiry)) {
            console.log('[APKConfig] Using cached Firebase config');
            return { ...DEFAULT_APK_CONFIG, ...JSON.parse(cachedConfig) };
        }

        console.log('[APKConfig] Fetching live config from Firebase...');

        // Fetch from Firebase REST API with cache-busting
        const cacheBuster = `?t=${Date.now()}`;
        const response = await fetch(
            `https://firestore.googleapis.com/v1/projects/luca-dffcd/databases/(default)/documents/config/apkVersion${cacheBuster}`,
            {
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache'
                }
            }
        );

        if (!response.ok) {
            console.log('[APKConfig] Firebase fetch failed, using local config');
            return getAPKConfig();
        }

        const data = await response.json();
        const fields = data.fields || {};

        // Parse Firebase config
        const firebaseConfig: Partial<APKConfig> = {
            version: fields.version?.stringValue,
            versionCode: fields.versionCode?.integerValue ? parseInt(fields.versionCode.integerValue) : undefined,
            downloadUrl: fields.downloadUrl?.stringValue,
            forceUpdate: fields.forceUpdate?.booleanValue || false,
            updateMessage: fields.updateMessage?.stringValue,
            releaseDate: fields.releaseDate?.stringValue,
            fileSize: fields.fileSize?.stringValue,
            changelog: (fields.changelog?.arrayValue?.values || []).map((v: any) => v.stringValue),
            isEnabled: fields.isEnabled?.booleanValue !== false, // Default true
        };

        // Merge with defaults
        const mergedConfig = { ...DEFAULT_APK_CONFIG, ...firebaseConfig };

        // Cache the result
        localStorage.setItem(CACHE_KEY, JSON.stringify(firebaseConfig));
        localStorage.setItem(CACHE_EXPIRY_KEY, (Date.now() + CACHE_DURATION).toString());

        console.log('[APKConfig] ✅ Fetched live config from Firebase:', mergedConfig.version);
        return mergedConfig;
    } catch (error) {
        console.error('[APKConfig] Firebase fetch error:', error);
        return getAPKConfig();
    }
}

/**
 * Force clear Firebase config cache
 * Call this when admin saves new config
 */
export function clearFirebaseConfigCache(): void {
    localStorage.removeItem('luca_apk_config_firebase');
    localStorage.removeItem('luca_apk_config_firebase_expiry');
    console.log('[APKConfig] Firebase config cache cleared');
}

/**
 * Clear all application caches
 * Useful for force-refreshing the APK when issues occur
 */
export async function clearAllCaches(): Promise<void> {
    try {
        console.log('[CacheBuster] Starting full cache clear...');

        // 1. Clear Service Worker caches
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
            console.log('[CacheBuster] Service Worker caches cleared:', cacheNames.length);
        }

        // 2. Clear Firebase config cache
        clearFirebaseConfigCache();

        // 3. Clear APK update cache
        localStorage.removeItem('luca_last_update_check');
        localStorage.removeItem('luca_update_dismissed');
        localStorage.removeItem('luca_apk_build_hash');
        localStorage.removeItem('luca_wasmer_etag');

        // 4. Unregister and re-register service workers
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
                await registration.unregister();
            }
            console.log('[CacheBuster] Service workers unregistered:', registrations.length);
        }

        console.log('[CacheBuster] ✅ All caches cleared successfully');
    } catch (error) {
        console.error('[CacheBuster] Cache clear error:', error);
    }
}

/**
 * Perform cache-busted refresh
 * Reloads the app with fresh resources
 */
export function performCacheBustedRefresh(): void {
    // Add timestamp to force bypass all caches
    const url = new URL(window.location.href);
    url.searchParams.set('_cb', Date.now().toString());
    window.location.href = url.toString();
}
